import React, { useCallback, useMemo, useState } from 'react';
import { RobbieDockedPanel, RobbieFloatingPanel, RobbieVoiceMessage } from '../../components/robbie';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import Section from './Section';

/** A silent WAV, built in-browser so the gallery has a real playable clip to demo the control against. */
const buildDemoClipUrl = (seconds: number): string => {
  const sampleRate = 8000;
  const dataSize = sampleRate * seconds * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeString = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
  };
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);
  return URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
};

/**
 * Stand-in waveforms and mic levels for the gallery only. Real clips carry
 * the peaks captured while recording (see `useVoiceRecorder`) — these exist
 * so the control's proportions can be judged without a mic.
 */
const demoShape = (count: number, seed: number): number[] =>
  Array.from({ length: count }, (_, i) => {
    const syllables = Math.abs(Math.sin(i * 0.95 + seed));
    const phrasing = 0.3 + 0.7 * Math.abs(Math.sin(i * 0.17 + seed * 1.7));
    return Math.round(Math.min(1, Math.max(0.05, syllables * phrasing * 1.25)) * 100) / 100;
  });

/**
 * /design — Robbie panel chrome, side by side with the wizard
 * mocks (docked rail) and `Dashboard v2.dc.html` (floating panel): waving
 * header, NEXT BEST STEP slot, message bubbles, quick actions, mic input;
 * thinking + disabled states; floating panel expand/collapse. Voice gets its
 * own section first: a sent clip, one at the recorder's cap, one replayed
 * from history (no clip bytes on that read), one recorded before waveforms
 * were kept, and the live recording row at both ends of its run. The first
 * docked panel and the floating panel wire a real `useVoiceRecorder` so the
 * mic button's LISTENING ring and recording toggle can be eyeballed here —
 * there's no chat backend on this page, so nothing is ever sent.
 */
const RobbiePanelGallery: React.FC = () => {
  const [draft, setDraft] = useState('');
  const [floatDraft, setFloatDraft] = useState('');

  const noop = useCallback(() => undefined, []);
  const docked = useVoiceRecorder({ onRecordingComplete: noop });
  const floating = useVoiceRecorder({ onRecordingComplete: noop });
  const shortClipUrl = useMemo(() => buildDemoClipUrl(3), []);
  const longClipUrl = useMemo(() => buildDemoClipUrl(60), []);
  const shortPeaks = useMemo(() => demoShape(40, 1.3), []);
  const longPeaks = useMemo(() => demoShape(40, 4.1), []);
  const demoLevels = useMemo(() => demoShape(28, 2.6), []);

  return (
    <>
    <Section title="Voice message + live recording row">
      <div className="flex flex-wrap items-start gap-s6">
        {/* Voice turns at rail width, on the panel's own wash, so the bubble is judged where it lives. */}
        <div className="flex w-robbie-rail flex-col gap-s4 rounded-card border border-line bg-ai-panel p-[12px]">
          {[
            { id: 'v1', label: 'Just sent — 3s', node: <RobbieVoiceMessage src={shortClipUrl} durationMs={3200} peaks={shortPeaks} /> },
            { id: 'v2', label: 'At the cap — 1:00', node: <RobbieVoiceMessage src={longClipUrl} durationMs={60000} peaks={longPeaks} /> },
            { id: 'v3', label: 'From history — clip not loaded', node: <RobbieVoiceMessage durationMs={8400} peaks={shortPeaks} /> },
            { id: 'v4', label: 'Recorded before waveforms were kept', node: <RobbieVoiceMessage src={shortClipUrl} durationMs={3200} /> },
          ].map((row) => (
            <div key={row.id} className="flex flex-col items-end gap-[3px]">
              <span className="text-[10px] uppercase tracking-[0.08em] text-ink-muted">{row.label}</span>
              <div className="w-[94%] max-w-[290px] rounded-card border border-line-strong bg-surface px-[11px] py-[9px]">
                {row.node}
              </div>
            </div>
          ))}
        </div>
        <div className="h-[560px] w-robbie-rail overflow-hidden rounded-card border border-line">
          <RobbieDockedPanel
            state="listening"
            subtitle="Listening…"
            messages={[{ id: 'm1', content: 'Go ahead — I’m listening.' }]}
            input={{ recording: true, recordingLevels: demoLevels, recordingElapsedMs: 7400, onAttach: () => undefined }}
          />
        </div>
        <div className="h-[560px] w-robbie-rail overflow-hidden rounded-card border border-line">
          <RobbieDockedPanel
            state="listening"
            subtitle="Listening…"
            messages={[{ id: 'm1', content: 'The last ten seconds warn before the cap cuts you off.' }]}
            input={{ recording: true, recordingLevels: demoLevels, recordingElapsedMs: 54200, onAttach: () => undefined }}
          />
        </div>
      </div>
    </Section>
    <Section title="Robbie panels (docked + floating)">
      <div className="flex flex-wrap items-start gap-s6">
        <div className="h-[560px] w-robbie-rail overflow-hidden rounded-card border border-line">
          <RobbieDockedPanel
            state={docked.status === 'recording' ? 'listening' : 'waving'}
            nextStep={{ label: 'Review 2 flagged BOM rows' }}
            messages={[
              {
                id: 'm1',
                content: (
                  <>
                    Found <strong>34 components</strong> in AMR-Drive-BOM-v3.xlsx. Two rows are missing quantities —
                    want me to flag them for you?
                  </>
                ),
              },
              { id: 'm2', content: 'I also matched the hub-motor datasheet to 6 of your drive components.' },
              {
                id: 'm3',
                role: 'user',
                variant: 'voice',
                content: <RobbieVoiceMessage src={shortClipUrl} durationMs={3200} peaks={shortPeaks} />,
              },
            ]}
            quickActions={[
              { id: 'q1', label: 'Show flagged rows' },
              { id: 'q2', label: 'Merge duplicate parts' },
            ]}
            input={{
              value: draft,
              onChange: setDraft,
              onAttach: () => undefined,
              onMic: docked.toggle,
              recording: docked.status === 'recording',
              recordingLevels: docked.levels,
              recordingElapsedMs: docked.elapsedMs,
              recordingMaxMs: docked.maxDurationMs,
            }}
          />
        </div>
        <div className="h-[560px] w-robbie-rail overflow-hidden rounded-card border border-line">
          <RobbieDockedPanel
            state="thinking"
            subtitle="Researching suppliers…"
            nextStep={{ label: 'Review supplier recommendations' }}
            working="Research is running — nothing needed from you right now."
            messages={[{ id: 'm1', content: 'I check every supplier against your criteria before it reaches you.' }]}
            input={{ placeholder: 'Ask Robbie…' }}
          />
        </div>
        <div className="h-[560px] w-robbie-rail overflow-hidden rounded-card border border-line">
          <RobbieDockedPanel
            state="thinking"
            subtitle="Checking supplier lead times…"
            messages={[{ id: 'm1', content: 'Give me a second — comparing the two quotes against your Q4 date.' }]}
            input={{ disabled: true, placeholder: 'Robbie is thinking…' }}
          />
        </div>
        <div className="flex w-[clamp(280px,26vw,340px)] flex-col gap-s6">
          <RobbieFloatingPanel
            state={floating.status === 'recording' ? 'listening' : 'insight'}
            insightCount={2}
            messages={[
              "The lowest AMR quote has a 9-week lead time that conflicts with your Q4 build. I'd split the award — want the numbers?",
            ]}
            input={{
              value: floatDraft,
              onChange: setFloatDraft,
              onMic: floating.toggle,
              recording: floating.status === 'recording',
              recordingLevels: floating.levels,
              recordingElapsedMs: floating.elapsedMs,
              recordingMaxMs: floating.maxDurationMs,
            }}
          />
          <RobbieFloatingPanel
            state="listening"
            messages={['Go ahead — I’m listening.']}
            input={{ recording: true, recordingLevels: demoLevels, recordingElapsedMs: 7400 }}
          />
          <span className="text-meta text-ink-muted">
            Collapse with the chevrons — the pill re-expands on click. Pages pin it bottom-right
            (absolute, clamp(280px,26vw,340px)) so it never blocks the canvas.
          </span>
        </div>
      </div>
    </Section>
    </>
  );
};

export default RobbiePanelGallery;
